<template>
  <div class="poi-details-container">
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <v-btn
                  icon
                  variant="text"
                  color="white"
                  size="small"
                  @click="goBack"
                >
                  <v-icon size="20">mdi-arrow-left</v-icon>
                </v-btn>
                
                <div class="ml-3">
                  <div class="text-h6 font-weight-semibold" style="color: #ffffff;">
                    {{ getPoiName() }}
                  </div>
                </div>

                <v-spacer />
              </div>
            </v-card>
          </v-sheet>

          <v-sheet color="transparent" class="mt-4">
            <v-row>
              <v-col cols="12" md="5" lg="4">
                <v-card rounded="xl" elevation="2" class="poi-image-card">
                  <div class="image-wrapper">
                    <v-img
                      :src="poiImage"
                      cover
                      class="poi-image"
                    >
                      <template #placeholder>
                        <div class="image-placeholder">
                          <v-icon size="64" color="#9AA6B1">mdi-image-outline</v-icon>
                        </div>
                      </template>
                    </v-img>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="7" lg="8">
                <v-card rounded="xl" elevation="2" class="pa-4 pa-md-6 poi-info-card">
                  <div class="poi-title mb-2">{{ poi?.name }}</div>
                  
                  <div class="info-grid">
                    <div class="info-item">
                      <v-icon color="#23A5E6" size="20">mdi-tag</v-icon>
                      <div class="info-content">
                        <div class="info-label">{{ $t('booking.category') }}</div>
                        <div class="info-value">{{ getCategoryDisplayName(poiCategory) }}</div>
                      </div>
                    </div>

                    <div class="info-item">
                      <v-icon color="#23A5E6" size="20">mdi-stairs</v-icon>
                      <div class="info-content">
                        <div class="info-label">{{ $t('booking.floor') }}</div>
                        <div class="info-value">{{ poi?.floor?.name }}</div>
                      </div>
                    </div>

                    <div class="info-item">
                      <v-icon color="#23A5E6" size="20">mdi-account-group</v-icon>
                      <div class="info-content">
                        <div class="info-label">{{ $t('booking.capacity') }}</div>
                        <div class="info-value">{{ poi?.capacity || 'N/A' }}</div>
                      </div>
                    </div>

                    <div class="info-item">
                      <v-icon color="#23A5E6" size="20">mdi-clock-outline</v-icon>
                      <div class="info-content">
                        <div class="info-label">{{ $t('office.time') }}</div>
                        <div class="info-value">
                          {{ poi?.availableFrom ? poi.availableFrom + ' - ' + poi.availableUntil : '06:00 - 20:00' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <v-btn
                    variant="flat"
                    color="#285096"
                    rounded="lg"
                    class="text-none mt-4"
                    prepend-icon="mdi-map-marker"
                    @click="openMap"
                  >
                    {{ $t('booking.show-map') }}
                  </v-btn>
                </v-card>
              </v-col>

              <v-col v-if="!isRestroom" cols="12">
                <v-card rounded="xl" elevation="2" class="pa-4 pa-md-6">
                  <div class="availability-header">
                    <v-btn
                      variant="outlined"
                      color="#285096"
                      rounded="lg"
                      class="text-none"
                      prepend-icon="mdi-calendar"
                      @click="availabilityDialog = true"
                    >
                      {{ moment(selectedDate).format('DD-MM-YYYY') }}
                    </v-btn>

                    <v-btn
                      variant="flat"
                      color="#28A745"
                      rounded="lg"
                      class="text-none font-weight-semibold"
                      prepend-icon="mdi-calendar-plus"
                      @click="goToBooking"
                    >
                      {{ $t('booking.schedule-now') }}
                    </v-btn>
                  </div>

                  <div class="availability-section">
                    <div class="availability-title-row">
                      <div class="availability-title">
                        <v-icon color="#285096" class="mr-2">mdi-clock-outline</v-icon>
                        <span>{{ formattedDate }} {{ $t('booking.availability') }}</span>
                      </div>

                      <div class="date-navigation">
                        <v-btn
                          icon
                          size="small"
                          variant="flat"
                          color="#285096"
                          :disabled="isToday"
                          @click="previousDay"
                        >
                          <v-icon size="18">mdi-chevron-left</v-icon>
                        </v-btn>

                        <v-btn
                          icon
                          size="small"
                          variant="flat"
                          color="#285096"
                          @click="nextDay"
                        >
                          <v-icon size="18">mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <v-progress-linear
                      v-if="showDateChangeLoader"
                      color="#23A5E6"
                      indeterminate
                      rounded
                      class="my-4"
                    />

                    <TimeSlot
                      v-else
                      :selectedDate="selectedDate"
                      :bookings="bookings"
                      class="mt-4"
                    />
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="availabilityDialog"
      max-width="400"
      persistent
    >
      <v-card rounded="xl">
        <v-card-title
          class="pa-4"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
        >
          <div class="d-flex align-center w-100">
            <v-icon color="white" class="mr-2">mdi-calendar</v-icon>
            <span class="text-h6 font-weight-semibold" style="color: white;">
              {{ $t('booking.select-date') }}
            </span>
            <v-spacer />
            <v-btn icon variant="text" color="white" size="small" @click="discardDate">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <div class="date-picker-wrapper">
            <DatePicker
              v-model="selectedDate"
              mode="date"
              :max-date="maxDate"
              :disabled-dates="disabledDates"
              :is-dark="false"
              color="#23A5E6"
              expanded
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4" style="background: #F8FAFC;">
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none"
            @click="discardDate"
          >
            {{ $t('office.cancel') }}
          </v-btn>

          <v-spacer />

          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none px-6"
            @click="confirmDate"
          >
            <v-icon start size="18">mdi-check</v-icon>
            {{ $t('office.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showIncidents"
      max-width="600"
      scrollable
    >
      <v-card rounded="xl">
        <v-card-title
          class="pa-4"
          style="background: linear-gradient(135deg, #DC3545 0%, #E04747 100%);"
        >
          <div class="d-flex align-center w-100">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            <span class="text-h6 font-weight-semibold" style="color: white;">
              {{ $t('incidents.current-incidents') }}
            </span>
            <v-spacer />
            <v-btn icon variant="text" color="white" size="small" @click="showIncidents = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0" style="max-height: 400px;">
          <v-list>
            <v-list-item
              v-for="(incident, index) in sortedIncidents"
              :key="index"
              class="py-3"
            >
              <template #prepend>
                <v-avatar
                  :color="getIncidentColor(incident.state)"
                  size="40"
                  variant="tonal"
                >
                  <v-icon :color="getIncidentColor(incident.state)">
                    {{ getIncidentIcon(incident.state) }}
                  </v-icon>
                </v-avatar>
              </template>

              <v-list-item-title class="font-weight-medium">
                {{ incident?.incidentCategoryName }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDate(incident.creationTime) }}
              </v-list-item-subtitle>

              <template #append>
                <v-chip
                  :color="getIncidentColor(incident.state)"
                  size="small"
                  variant="flat"
                  class="text-capitalize"
                >
                  {{ incident.state }}
                </v-chip>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <MapView ref="mapView" />
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import lodash from 'lodash'
import { debounce } from 'lodash'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import Loader from '@/components/general/Loader.vue'
import { DatePicker } from 'v-calendar'
import MapView from '@/components/general/MapView.vue'
import TimeSlot from '@/components/general/ChildComponents/TimeSlot.vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { useOAuthService } from '@/services/OAuthService'
import { getCookie } from '@/services/utils'

import {
  getFloorById,
  getPoiCategoryById,
  getPoiById,
  getBuildings,
  isStaingOrQa,
  getReservationsByPoi,
} from '@/controllers/BaseController'
import { getHardwareByType, getElevators } from '@/controllers/BackboneAPI'

export default {
  name: 'PoiDetails',
  components: {
    Loader,
    TimeSlot,
    DatePicker,
    MapView,
  },
  setup() {
    const { locale } = useI18n()
    const router = useRouter()
    const showLoader = ref(true)
    const showDateChangeLoader = ref(false)
    const OAuthService = useOAuthService()
    const poi = ref({})
    const selectedBuilding = ref({})
    const mapView = ref(null)
    const poiCategory = ref({})
    const bookings = ref([])
    const showIncidents = ref(false)
    const availabilityDialog = ref(false)
    const selectedDate = ref(new Date())
    const poiImage = ref('')
    const isRestroom = ref(false)
    const userProfile = ref({})
    const currentLanguage = ref('en')

    const backupImageMap = {
      'meeting room': require('@/assets/PoiImages/backup_poi_images/meeting_room.png'),
      'lab': require('@/assets/PoiImages/backup_poi_images/lab_room.png'),
      'restrooms': require('@/assets/PoiImages/backup_poi_images/rest_room.png'),
      'printer': require('@/assets/PoiImages/backup_poi_images/printer_room.png'),
      'studio': require('@/assets/PoiImages/backup_poi_images/studio.png'),
      'lager': require('@/assets/PoiImages/backup_poi_images/lager_room.png'),
      'food and beverages': require('@/assets/PoiImages/backup_poi_images/kitchen_room.png'),
      'event space': require('@/assets/PoiImages/backup_poi_images/event_space.png'),
      'showers': require('@/assets/PoiImages/backup_poi_images/shower.png'),
    }

    const disabledDates = ref([
      {
        repeat: {
          weekdays: [7, 1],
        },
      },
    ])

    let incidents = ref([])

    const getCategoryDisplayName = (category) => {
      if (!category) return ''
      if (currentLanguage.value === 'de' && category.name_de) {
        return category.name_de
      }
      return category.name
    }

    const getCategoryImage = (categoryName) => {
      const normalizedName = categoryName.toLowerCase().trim()
      
      if (backupImageMap[normalizedName]) {
        return backupImageMap[normalizedName]
      }
      
      for (const [key, image] of Object.entries(backupImageMap)) {
        if (normalizedName.includes(key) || key.includes(normalizedName)) {
          return image
        }
      }
      
      return 'https://via.placeholder.com/300'
    }

    const loadLanguageFromCookie = async () => {
      const savedLanguage = await getCookie('language')
      if (savedLanguage) {
        currentLanguage.value = savedLanguage
        locale.value = savedLanguage
      }
    }

    const goBack = () => router.back()

    const maxDate = computed(() => {
      const today = new Date()
      return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 57)
    })

    const isToday = computed(() => moment(selectedDate.value).isSame(moment(), 'day'))

    const formattedDate = computed(() => moment(selectedDate.value).format('DD MMMM YYYY'))

    const sortedIncidents = computed(() => lodash.orderBy(incidents.value, ['incidentCategoryName'], ['asc']))

    const discardDate = () => {
      availabilityDialog.value = false
      selectedDate.value = new Date()
    }

    const confirmDate = async () => {
      availabilityDialog.value = false
      await handleAvailability()
    }

    const handleAvailability = async () => {
      try {
        showDateChangeLoader.value = true
        let start = moment(selectedDate.value).startOf('day').toDate()
        let end = moment(selectedDate.value).endOf('day').toDate()
        let poiBookings = await getReservationsByPoi(poi.value.id, start, end)
        
        poiBookings.forEach(reservation => {
          let creatorNameFirstWord = reservation?.creatorName ? reservation.creatorName.split(' ')[0].toLowerCase() : null
          let creatorEmailPart = reservation.creatorEmail.split('@')[0].toLowerCase()
          let emailParts = creatorEmailPart.split('.')
          let name
          
          if (creatorNameFirstWord && emailParts.includes(creatorNameFirstWord)) {
            name = emailParts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
          } else {
            name = emailParts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
          }
        })
        
        bookings.value = poiBookings
      } catch (error) {
        console.error(error)
      } finally {
        showDateChangeLoader.value = false
      }
    }

    const getPoiName = () => poi.value.name

    const openMap = () => {
      let BUILDING = JSON.parse(JSON.stringify(selectedBuilding.value))
      let Category = JSON.parse(JSON.stringify(poiCategory.value))
      let POI = lodash.cloneDeepWith(poi.value, value => (typeof value === 'object' && new WeakSet().has(value) ? undefined : undefined))
      mapView.value.openMap(BUILDING, [POI], [Category])
    }

    const goToBooking = () => {
      router.push({
        name: 'createManageBooking',
        params: {
          building_id: poi.value.buildingId,
          category_id: poi.value.poiCategories.ids[0],
          poi_id: poi.value.id,
        },
      })
    }

    const nextDay = async () => {
      selectedDate.value = moment(selectedDate.value).add(1, 'days').toDate()
      await handleAvailability()
    }

    const previousDay = async () => {
      selectedDate.value = moment(selectedDate.value).subtract(1, 'days').toDate()
      await handleAvailability()
    }

    const formatDate = date => moment(date).format('DD.MM.YYYY HH:mm')
    
    const getIncidentColor = state => {
      if (state === 'open' || state === 'Open') return '#DC3545'
      if (state === 'done' || state === 'Done') return '#28A745'
      return '#23A5E6'
    }

    const getIncidentIcon = state => {
      if (state === 'open' || state === 'Open') return 'mdi-alert-circle'
      if (state === 'done' || state === 'Done') return 'mdi-check-circle'
      return 'mdi-information'
    }

    onMounted(async () => {
      const resizeListener = debounce(() => window.innerWidth, 200)
      window.addEventListener('resize', resizeListener)
      
      try {
        await loadLanguageFromCookie()
        
        userProfile.value = await OAuthService.getUserProfile()
        const fetchedPoi = await getPoiById(router.currentRoute.value.params.poi_id)
        let isPIA = await isStaingOrQa()
        let buildings = await getBuildings()
        selectedBuilding.value = buildings.find(building => building.id === fetchedPoi.buildingId)
        const fetchedCategory = await getPoiCategoryById(fetchedPoi.poiCategories.ids[0])
        
        if (
          fetchedCategory.name?.toLowerCase()?.includes('rest') ||
          fetchedCategory.name?.toLowerCase()?.includes('toilet') ||
          fetchedCategory.name?.toLowerCase()?.includes('wc')
        ) {
          isRestroom.value = true
        }

        let floor = await getFloorById(fetchedPoi.floors.ids[0])
        fetchedPoi['floor'] = floor
        
        const localTimezone = 'Europe/Berlin'
        let today = moment().tz(localTimezone).format('YYYY-MM-DD')
        
        if (isPIA) {
          fetchedPoi.availableFrom = '06:00'
          fetchedPoi.availableUntil = '20:00'
        }

        if (fetchedPoi?.configs['room-control'] !== undefined && fetchedPoi?.configs['room-control'] === 'true') {
          const hardwareTypes = ['BLINDS', 'VENTILATOR', 'LIGHT', 'HEATER']
          const hardwarePromises = hardwareTypes.map(type => getHardwareByType(fetchedPoi.buildingId, type).catch(() => []))
          const hardwareArrays = await Promise.all(hardwarePromises)
          const hardwareData = Object.fromEntries(hardwareTypes.map((type, index) => [type, hardwareArrays[index]]))
          
          fetchedPoi['LIGHT'] = hardwareData['LIGHT'].filter(light => light.configs?.['poi-id'] === fetchedPoi.id)
          fetchedPoi['VENTILATOR'] = hardwareData['VENTILATOR'].filter(ventilator => ventilator.configs?.['poi-id'] === fetchedPoi.id)
          fetchedPoi['HEATER'] = hardwareData['HEATER'].filter(heater => heater.configs?.['poi-id'] === fetchedPoi.id)
          fetchedPoi['BLINDS'] = hardwareData['BLINDS'].filter(blind => blind.configs?.['poi-id'] === fetchedPoi.id)
          fetchedPoi['hasHardware'] = true
        }

        let elevators = await getElevators(fetchedPoi.buildingId)
        let entrances = []
        
        elevators.forEach((elevator) => {
          let ENTRANCES = elevator.entrances
          ENTRANCES.forEach(entrance => {
            if (entrance?.location?.floorId === floor.id) {
              entrance['elevator'] = elevator
              entrances.push(entrance)
            }
          })
        })
        
        fetchedPoi['entrances'] = entrances
        if (entrances?.length > 0) {
          fetchedPoi['hasEntrances'] = true
        }

        poi.value = fetchedPoi
        poiCategory.value = fetchedCategory
        
        poiImage.value = getCategoryImage(fetchedCategory.name)
        
        await handleAvailability()
      } catch (error) {
        console.error(error)
      } finally {
        showLoader.value = false
        onUnmounted(() => window.removeEventListener('resize', resizeListener))
      }
    })

    return {
      goBack,
      getPoiName,
      poiImage,
      openMap,
      poi,
      goToBooking,
      showIncidents,
      formatDate,
      getIncidentColor,
      getIncidentIcon,
      sortedIncidents,
      mapView,
      poiCategory,
      disabledDates,
      selectedBuilding,
      isRestroom,
      showLoader,
      showDateChangeLoader,
      availabilityDialog,
      selectedDate,
      moment,
      maxDate,
      handleAvailability,
      discardDate,
      confirmDate,
      bookings,
      incidents,
      isToday,
      nextDay,
      previousDay,
      formattedDate,
      userProfile,
      OAuthService,
      getCategoryDisplayName,
      currentLanguage,
    }
  },
}
</script>

<style scoped>
.poi-details-container {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);
}

.poi-image-card {
  overflow: hidden;
  height: 100%;
  min-height: 300px;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.poi-image {
  width: 100%;
  height: 100%;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.poi-image-card:hover .poi-image {
  transform: scale(1.05);
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: linear-gradient(135deg, #F3F6FF 0%, #FFFFFF 100%);
}

.poi-info-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.poi-title {
  font-size: 24px;
  font-weight: 700;
  color: #001437;
  line-height: 1.3;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #F8FAFC;
  border-radius: 12px;
}

.info-content {
  flex: 1;
}

.info-label {
  font-size: 11px;
  color: #9AA6B1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 15px;
  color: #285096;
  font-weight: 600;
  line-height: 1.3;
}

.availability-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.availability-section {
  padding: 20px;
  background: linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%);
  border-radius: 16px;
  border: 1px solid #E2E8F0;
}

.availability-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.availability-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #285096;
}

.date-navigation {
  display: flex;
  gap: 8px;
}

.v-card {
  animation: fadeInUp 0.4s ease-out backwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.date-picker-wrapper {
  display: flex;
  justify-content: center;
  padding: 8px;
}

.date-picker-wrapper :deep(.vc-container) {
  border: none;
  box-shadow: none;
  font-family: 'Inter', sans-serif;
}

.date-picker-wrapper :deep(.vc-header) {
  padding: 12px 16px;
}

.date-picker-wrapper :deep(.vc-title) {
  font-size: 16px;
  font-weight: 600;
  color: #285096;
}

.date-picker-wrapper :deep(.vc-weeks) {
  padding: 8px;
}

.date-picker-wrapper :deep(.vc-weekday) {
  color: #9AA6B1;
  font-size: 12px;
  font-weight: 600;
}

.date-picker-wrapper :deep(.vc-day) {
  margin: 2px;
}

.date-picker-wrapper :deep(.vc-day-content) {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #001437;
}

.date-picker-wrapper :deep(.vc-day-content:hover) {
  background-color: rgba(35, 165, 230, 0.1);
}

.date-picker-wrapper :deep(.vc-day.is-today .vc-day-content) {
  background-color: rgba(35, 165, 230, 0.15);
  color: #23A5E6;
  font-weight: 700;
}

.date-picker-wrapper :deep(.vc-highlight) {
  background-color: #23A5E6 !important;
  border-radius: 8px;
}

.date-picker-wrapper :deep(.vc-day.is-selected .vc-day-content) {
  background-color: #23A5E6;
  color: white;
  font-weight: 700;
}

.date-picker-wrapper :deep(.vc-day.is-disabled .vc-day-content) {
  color: #CBD5E0;
  opacity: 0.4;
  cursor: not-allowed;
}

.date-picker-wrapper :deep(.vc-arrow) {
  color: #285096;
  border-radius: 6px;
  padding: 6px;
}

.date-picker-wrapper :deep(.vc-arrow:hover) {
  background-color: rgba(35, 165, 230, 0.1);
}

@media (max-width: 960px) {
  .poi-title {
    font-size: 20px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .availability-header {
    flex-direction: column;
    align-items: stretch;
  }

  .availability-header .v-btn {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .poi-image-card {
    min-height: 250px;
  }

  .poi-title {
    font-size: 18px;
  }

  .availability-title {
    font-size: 14px;
  }

  .availability-section {
    padding: 12px;
  }

  .poi-details-container :deep(.v-container) {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}
</style>
